import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  Loader2, Wallet, ExternalLink, CheckCircle2, XCircle, Clock, AlertTriangle, Zap,
} from "lucide-react";
import { toast } from "sonner";

const statusPill = (s) => {
  const m = {
    queued:    { label: "Queued",    cls: "text-amber-300 bg-amber-500/10 border-amber-500/30",   icon: Clock },
    approved:  { label: "Approved",  cls: "text-sky-300 bg-sky-500/10 border-sky-500/30",         icon: CheckCircle2 },
    sending:   { label: "Sending…",  cls: "text-sky-300 bg-sky-500/10 border-sky-500/30",         icon: Loader2 },
    sent:      { label: "Sent",      cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", icon: CheckCircle2 },
    rejected:  { label: "Rejected",  cls: "text-rose-300 bg-rose-500/10 border-rose-500/30",      icon: XCircle },
    failed:    { label: "Failed",    cls: "text-rose-300 bg-rose-500/10 border-rose-500/30",      icon: AlertTriangle },
  };
  return m[s] || { label: s, cls: "text-zinc-400 bg-zinc-500/10 border-zinc-500/30", icon: Clock };
};

const AdminReferrals = () => {
  const [drafts, setDrafts] = useState([]);
  const [mode, setMode] = useState("dry-run");
  const [wallet, setWallet] = useState(null);
  const [manualQueue, setManualQueue] = useState([]);
  const [threshold, setThreshold] = useState(25);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null); // draft_id being rejected
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [dR, wR, pR] = await Promise.all([
        api.get("/admin/referrals/drafts"),
        api.get("/admin/referrals/wallet"),
        api.get("/admin/referrals/pending-payouts"),
      ]);
      setDrafts(dR.data.drafts || []);
      setMode(dR.data.mode || "dry-run");
      setWallet(wR.data);
      setManualQueue(pR.data.queue || []);
      setThreshold(pR.data.threshold_usd || 25);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (draft) => {
    setBusyId(draft.draft_id);
    try {
      const r = await api.post(`/admin/referrals/drafts/${draft.draft_id}/approve`);
      const tx = r.data?.tx_hash || "";
      toast.success(`Broadcast ${tx.slice(0, 12)}…`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async () => {
    if (!rejectFor) return;
    setBusyId(rejectFor);
    try {
      await api.post(`/admin/referrals/drafts/${rejectFor}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setRejectFor(null); setRejectReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  const queued = drafts.filter((d) => d.status === "queued");
  const history = drafts.filter((d) => d.status !== "queued");

  return (
    <div data-testid="admin-referrals-page">
      <PageHeader
        overline="Admin"
        title="Referral payouts"
        description="Automated USDC drafts on Base. Review before broadcast."
      />

      {/* Wallet + mode indicator */}
      <div className="grid md:grid-cols-3 gap-0 border-t border-l border-[#2c241e]">
        <div className="border-r border-b border-[#2c241e] p-5" data-testid="wallet-mode-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Mode</div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 border text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded ${
                mode === "live"
                  ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
                  : "text-amber-300 bg-amber-500/10 border-amber-500/30"
              }`}
            >
              <Zap size={10} /> {mode}
            </span>
            <div className="text-xs text-zinc-500">
              {mode === "live" ? "Broadcasting on Base" : "Simulated tx hashes"}
            </div>
          </div>
          {mode !== "live" && (
            <div className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
              Set <code>BASE_RPC_URL</code>, <code>HOT_WALLET_ADDRESS</code>, and{" "}
              <code>HOT_WALLET_PRIVATE_KEY</code> in the backend env to enable live payouts.
            </div>
          )}
        </div>
        <div className="border-r border-b border-[#2c241e] p-5" data-testid="wallet-eth-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Hot wallet ETH</div>
          <div className="font-mono text-lg text-white">
            {wallet ? wallet.eth.toFixed(6) : "—"}{" "}
            <span className="text-xs text-zinc-500">ETH</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1 truncate">{wallet?.wallet || "no wallet"}</div>
        </div>
        <div className="border-b border-[#2c241e] p-5" data-testid="wallet-usdc-card">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Hot wallet USDC</div>
          <div className="font-mono text-lg text-white">
            {wallet ? wallet.usdc.toFixed(2) : "—"}{" "}
            <span className="text-xs text-zinc-500">USDC</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">Base network</div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="p-4 md:p-8 space-y-10">
          {/* Awaiting approval */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Awaiting approval</div>
                <div className="text-sm text-zinc-400 mt-0.5">
                  Auto-drafted by the scanner when a referrer crosses ${threshold} available.
                </div>
              </div>
              <div className="text-sm text-zinc-500">
                {queued.length} draft{queued.length === 1 ? "" : "s"}
              </div>
            </div>
            {queued.length === 0 ? (
              <div className="border border-[#2c241e] p-10 text-center text-sm text-zinc-500" data-testid="drafts-empty">
                No payouts awaiting approval. Scanner runs every ~60s.
              </div>
            ) : (
              <div className="space-y-3" data-testid="drafts-list">
                {queued.map((d) => {
                  const busy = busyId === d.draft_id;
                  return (
                    <div
                      key={d.draft_id}
                      data-testid={`draft-row-${d.draft_id}`}
                      className="border border-[#2c241e] p-5 grid md:grid-cols-12 gap-4 items-start"
                    >
                      <div className="md:col-span-4 min-w-0">
                        <div className="text-white truncate">
                          {d.referrer_name || d.referrer_email || d.referrer_user_id}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">{d.referrer_email}</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-3">Pay to</div>
                        <div className="text-[11px] font-mono text-zinc-400 break-all">
                          {d.recipient_address}
                        </div>
                      </div>
                      <div className="md:col-span-3">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Amount</div>
                        <div className="font-mono text-2xl text-white" data-testid={`draft-amount-${d.draft_id}`}>
                          {fmtMoney(d.amount_usd)}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {d.credit_ids.length} credit{d.credit_ids.length === 1 ? "" : "s"} · {d.chain} · {d.asset?.toUpperCase()}
                        </div>
                        <div className="text-[10px] text-zinc-600 mt-1">Drafted {fmtDate(d.created_at)}</div>
                      </div>
                      <div className="md:col-span-5 flex flex-col md:items-end gap-2">
                        {rejectFor === d.draft_id ? (
                          <>
                            <input
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="reason (optional)"
                              className="w-full md:w-72 bg-[#0A0A0A] border border-[#2c241e] px-3 py-1.5 text-xs focus:outline-none focus:border-white"
                              data-testid={`reject-reason-${d.draft_id}`}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => { setRejectFor(null); setRejectReason(""); }}
                                className="text-xs text-zinc-500 hover:text-white px-3 py-1.5"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={doReject}
                                disabled={busy}
                                data-testid={`confirm-reject-${d.draft_id}`}
                                className="inline-flex items-center gap-1 bg-rose-500/20 text-rose-300 border border-rose-500/30 text-xs px-3 py-1.5 rounded hover:bg-rose-500/30 disabled:opacity-50"
                              >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                                Confirm reject
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => setRejectFor(d.draft_id)}
                              disabled={busy}
                              data-testid={`reject-btn-${d.draft_id}`}
                              className="inline-flex items-center gap-1 border border-[#2c241e] text-zinc-400 text-xs px-3 py-1.5 rounded hover:border-rose-500/50 hover:text-rose-300 disabled:opacity-50"
                            >
                              <XCircle size={12} /> Reject
                            </button>
                            <button
                              onClick={() => approve(d)}
                              disabled={busy}
                              data-testid={`approve-btn-${d.draft_id}`}
                              className="inline-flex items-center gap-2 bg-white text-black text-xs font-medium px-4 py-1.5 rounded hover:bg-zinc-200 disabled:opacity-50"
                            >
                              {busy ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                              {busy ? "Broadcasting…" : mode === "live" ? "Approve & broadcast" : "Approve (dry-run)"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* History */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Recent payout drafts</div>
            {history.length === 0 ? (
              <div className="text-xs text-zinc-500">No history yet.</div>
            ) : (
              <div className="border border-[#2c241e]" data-testid="draft-history">
                <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#2c241e] text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  <div className="col-span-3">Drafted</div>
                  <div className="col-span-3">Referrer</div>
                  <div className="col-span-2 text-right">Amount</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Tx</div>
                </div>
                {history.slice(0, 30).map((d) => {
                  const pill = statusPill(d.status);
                  const Icon = pill.icon;
                  return (
                    <div
                      key={d.draft_id}
                      data-testid={`history-row-${d.draft_id}`}
                      className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#2c241e] last:border-b-0 items-center text-sm"
                    >
                      <div className="col-span-3 text-xs text-zinc-500">{fmtDate(d.created_at)}</div>
                      <div className="col-span-3 truncate">{d.referrer_name || d.referrer_email || "—"}</div>
                      <div className="col-span-2 text-right font-mono">{fmtMoney(d.amount_usd)}</div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center gap-1 border text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded ${pill.cls}`}>
                          <Icon size={10} className={d.status === "sending" ? "animate-spin" : ""} /> {pill.label}
                        </span>
                      </div>
                      <div className="col-span-2 text-[11px] font-mono text-zinc-500 truncate" title={d.tx_hash || ""}>
                        {d.tx_hash ? (d.tx_hash.startsWith("0xsim") ? "0xsim…" : d.tx_hash.slice(0, 14) + "…") : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Below-threshold section (still surfaces users who can't yet auto-draft) */}
          {manualQueue.filter((r) => r.below_threshold || !r.crypto_address).length > 0 && (
            <section data-testid="ineligible-referrers">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Below threshold / no wallet</div>
              <div className="border border-[#2c241e]">
                {manualQueue.filter((r) => r.below_threshold || !r.crypto_address).map((row) => (
                  <div
                    key={row.referrer_user_id}
                    className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[#2c241e] last:border-b-0 items-center text-sm"
                  >
                    <div className="col-span-6 truncate">
                      <div className="text-white">{row.name || row.email || row.referrer_user_id}</div>
                      <div className="text-xs text-zinc-500 truncate">{row.email}</div>
                    </div>
                    <div className="col-span-3 text-right font-mono">{fmtMoney(row.amount_usd)}</div>
                    <div className="col-span-3 text-right text-xs">
                      {!row.crypto_address ? (
                        <span className="inline-flex items-center gap-1 text-rose-400">
                          <ExternalLink size={11} /> no wallet
                        </span>
                      ) : (
                        <span className="text-amber-300">below ${threshold}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminReferrals;
